import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { getSalesByDiscountCoupons } from "../../../Services/authService";

const DiscountsCouponsChartData = ({
  selectedLocations,
  dateRange,
  applyClicked,
}) => {
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();

  const colorMapping = {
    "Net Total": "#4C33DB",
    Coupons: "#FF8F8F",
    Discounts: "#78F080",
  };

  const totalColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const grayscaleColor =
    mode === "light" ? LIGHTCOLORS.GreyScale30 : DARKCOLORS.GreyScale30;
  const dividerColor =
    mode === "light" ? LIGHTCOLORS.GreyScale30 : DARKCOLORS.GreyScale30;
  const netSalesHighlight =
    mode === "light" ? LIGHTCOLORS.GreyScale10 : DARKCOLORS.GreyScale10;
  const [data, setData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasData = data.length > 0 && data.some((d) => d.value > 0);
  const chartData = hasData
    ? data
    : [{ name: "", value: 1, color: grayscaleColor }];

  useEffect(() => {
    const fetchData = async () => {
      setError(null);
      try {
        const formatLocalDate = (date) => {
          if (!date) return null;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const startDate = dateRange[0]?.startDate
          ? formatLocalDate(new Date(dateRange[0].startDate))
          : null;
        const endDate = dateRange[0]?.endDate
          ? formatLocalDate(new Date(dateRange[0].endDate))
          : null;

        const response = await getSalesByDiscountCoupons(
          startDate,
          endDate,
          selectedLocations,
        );

        const r = response || {};
        const transformedData = [
          {
            name: t("sales_analytics.netCoupons"),
            value: r.salesByCoupon || 0,
            color: colorMapping["Coupons"],
          },
          {
            name: t("sales_analytics.netDiscounts"),
            value: r.salesByDiscount || 0,
            color: colorMapping["Discounts"],
          },
          {
            name: t("sales_analytics.netTotal"),
            value: r.totalSales || 0,
            color: colorMapping["Net Total"],
          },
        ];

        setData(transformedData);
        setTotalAmount(
          (r.totalSales || 0) +
            (r.salesByCoupon || 0) +
            (r.salesByDiscount || 0),
        );
      } catch (err) {
        setError(t("sales_analytics.errorLoadingSalesData"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedLocations, applyClicked, i18n.language]);

  return (
    <div>
      <h3 className="subheading-sec-card" style={{ marginBottom: "0.2rem" }}>
        {t("sales_analytics.SalesCouponsDiscounts")}
      </h3>

      <div
        className="payment-discount-sec"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <PieChart width={240} height={240}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={110}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `€ ${value.toFixed(2)}`} />
          </PieChart>
        </div>

        {/* Right Side - Legend */}
        <div
          className="payment-discount-legend"
          style={{
            flex: 1,
            paddingLeft: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {data.map((entry, index) => {
            const isNetSales = index === data.length - 1;

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  boxSizing: "border-box",

                  padding: "8px 10px",
                  borderRadius: isNetSales ? "8px" : "0px",
                  backgroundColor: isNetSales
                    ? netSalesHighlight
                    : "transparent",
                }}
              >
                {/* Dot + Label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: entry.color,
                      borderRadius: "50%",
                      marginRight: "20px",
                      flexShrink: 0,
                    }}
                  />

                  <span
                    className="popup-text"
                    style={{
                      textAlign: "left",
                      margin: 0,
                    }}
                  >
                    {entry.name}
                  </span>
                </div>

                {/* Amount */}
                <span
                  className="popup-text1"
                  style={{
                    textAlign: "left",
                    minWidth: "60px",
                    margin: 0,
                  }}
                >
                  € {entry.value.toFixed(2)}
                </span>
              </div>
            );
          })}

          {/* Divider */}
          <hr
            style={{
              border: "none",
              borderTop: `1px solid ${dividerColor}`,
              margin: "4px 0 13px",
            }}
          />

          {/* Total Amount Display */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              id="margin-bottom"
              className="popup-text"
              style={{ textAlign: "left", margin: "0px", fontWeight: 600 }}
            >
              {t("sales_analytics.total")}
            </span>
            <span
              className="popup-text1"
              style={{ textAlign: "left", minWidth: "60px", fontWeight: 600 }}
            >
              € {totalAmount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountsCouponsChartData;
